<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { sessionExpiresAtMs } from '@/shared/services/session/session-expiry-warning.service'

const WARNING_THRESHOLD_MS = 5 * 60 * 1000
const DANGER_THRESHOLD_MS = 60 * 1000

const isEnabled = import.meta.env.VITE_SESSION_COUNTDOWN === 'true'

const now = ref(Date.now())
let ticker = null

onMounted(() => {
  ticker = setInterval(() => {
    now.value = Date.now()
  }, 1000)
})

onBeforeUnmount(() => {
  clearInterval(ticker)
})

const remainingMs = computed(() => {
  if (!sessionExpiresAtMs.value)
    return null
  return Math.max(0, sessionExpiresAtMs.value - now.value)
})

const label = computed(() => {
  if (remainingMs.value === null)
    return ''
  const totalSeconds = Math.floor(remainingMs.value / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const mmss = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  return hours > 0 ? `${hours}:${mmss}` : mmss
})

const severityClass = computed(() => {
  if (remainingMs.value === null)
    return ''
  if (remainingMs.value <= DANGER_THRESHOLD_MS)
    return 'text-dss-error-600 animate-pulse'
  if (remainingMs.value <= WARNING_THRESHOLD_MS)
    return 'text-dss-warning-600'
  return 'text-dss-secondary-500'
})
</script>

<template>
  <div
    v-if="isEnabled && remainingMs !== null"
    class="flex items-center gap-1.5 text-sm font-mono tabular-nums select-none"
    :class="severityClass"
    title="Session expires in"
    data-testid="session-countdown"
  >
    <i class="pi pi-clock text-xs" />
    <span>{{ label }}</span>
  </div>
</template>
